/**
 * UX-022 Foundational validation & required-fields model — shared field markers.
 *
 * One vocabulary, everywhere:
 *   - purple rail (brand-600, UX-004 token) on the LEFT of the field group = this
 *     required field still owes input
 *   - green check (success-600), inline by the label = this required field is satisfied
 *   - neutral "Optional" tag, inline by the label = the field is optional
 *
 * The asterisk is retired as the primary required marker in favor of the rail.
 * The rail is **default-on**: a required-but-empty field carries it from first
 * render (the rail means "required", it does not wait for an error). Once the
 * field is satisfied the rail is replaced by the inline green check.
 */

import { Check } from 'flowbite-react-icons/outline'

import type { FieldMarkerState } from './fieldMarkerState'

interface FieldMarkerProps {
  state: FieldMarkerState
  /** Accessible label prefix (kept for API symmetry; markers are decorative). */
  fieldLabel?: string
  /**
   * When true, the `owes` state renders a small inline brand swatch instead of
   * nothing. Reserved for section/group *titles* that cannot host the full-height
   * {@link FieldRequiredRail} on a field group.
   */
  inlineOwesSwatch?: boolean
}

/**
 * Inline marker rendered next to a field label. `satisfied` → green check,
 * `optional` → neutral "Optional" tag. The `owes` (required-empty) state renders
 * nothing inline — it is conveyed by the full-height {@link FieldRequiredRail} on
 * the left of the field group instead.
 *
 * The marker is intentionally **decorative** (`aria-hidden`): it lives inside
 * the field's `<label>`, so any readable text here would be concatenated into
 * the control's accessible name (e.g. "First name required"). Requiredness /
 * validity is already conveyed to assistive tech via `aria-invalid`, the
 * `required` attribute, and the per-field error message — the rail/check is a
 * visual reinforcement of that, not the source of truth.
 */
export function FieldMarker({ state, inlineOwesSwatch = false }: FieldMarkerProps) {
  if (state === 'optional') {
    return <OptionalTag />
  }
  if (state === 'satisfied') {
    return (
      <Check
        aria-hidden="true"
        className="ml-1 inline-block h-3.5 w-3.5 shrink-0 align-text-bottom text-success-600 dark:text-success-500"
      />
    )
  }
  // 'owes': normally nothing inline (the left rail carries the signal). Section /
  // group *titles* have no field group to host a rail, so they may opt into a
  // small inline brand swatch via `inlineOwesSwatch`.
  if (state === 'owes' && inlineOwesSwatch) {
    return (
      <span
        aria-hidden="true"
        className="ml-1 inline-block h-3.5 w-1 shrink-0 rounded-full bg-brand-600 align-text-bottom dark:bg-brand-500"
      />
    )
  }
  // 'owes' (rail-backed) and 'none': nothing inline.
  return null
}

/**
 * Full-height brand-600 rail (UX-004 token) rendered on the LEFT of a required
 * field group, spanning the whole group (label + input + message). Shown while
 * the field still owes input; hidden once satisfied/optional. Decorative
 * (`aria-hidden`) — requiredness is already exposed to assistive tech via the
 * control's `required`/`aria-invalid` attributes.
 *
 * Usage: wrap the field group in a flex row and render this as the first child,
 * or place it as an absolutely-positioned bar inside a `relative` group. It
 * renders `null` for non-`owes` states so callers can mount it unconditionally.
 */
export function FieldRequiredRail({ state, className }: { state: FieldMarkerState; className?: string }) {
  if (state !== 'owes') return null
  return (
    <span
      aria-hidden="true"
      className={`w-1 shrink-0 self-stretch rounded-full bg-brand-600 dark:bg-brand-500 ${className ?? ''}`}
    />
  )
}

/**
 * Neutral gray "Optional" tag (UX-015): reserved strictly for optional fields —
 * never green, which is reserved for satisfied-required. Decorative
 * (`aria-hidden`) when rendered inside a field label so it does not pollute the
 * control's accessible name.
 */
export function OptionalTag({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`ml-2 inline-flex items-center rounded-full bg-background-gray px-1.5 py-0.5 text-xs font-medium leading-4 text-text-body-subtle dark:bg-gray-800 dark:text-gray-400 ${className ?? ''}`}
    >
      Optional
    </span>
  )
}
